// ============================================================================
// BUILD NOT-YET EXPLANATION (NO PHI)
//
// Pure function. Generates a denial-rationale summary for the referring office,
// explaining why a referral did not meet criteria and what might change that.
//
// ZERO PHI: no patient name / DOB / MRN / contact. Only specialty, pack,
// criteria labels, and next-step descriptions.
// ============================================================================

/**
 * Build a not-yet explanation / denial rationale message.
 *
 * @param {object} params
 * @param {string|null} params.receiptToken          - RG-XXXXXXX stable token
 * @param {string}      params.referralId            - Firestore referralId (fallback)
 * @param {string}      params.specialtyName         - Human-readable specialty name
 * @param {string}      params.packName              - Pack / clinical pathway name
 * @param {string[]}    params.notYetReasons         - Array of reason / next-step labels
 * @param {string[]}    params.missingItemTypeLabels - Items that could change the outcome
 * @param {string|null} params.submittedAt           - ISO timestamp of submission
 * @returns {string} Plain-text message ready for clipboard
 */
export function buildNotYetExplanationNoPhi({
  receiptToken = null,
  referralId = '',
  specialtyName = 'Specialty',
  packName = '',
  notYetReasons = [],
  missingItemTypeLabels = [],
  submittedAt = null,
}) {
  const ref = receiptToken || (referralId ? referralId.slice(0, 12) : 'N/A');

  const dateStr = submittedAt
    ? new Date(submittedAt).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : 'recently';

  const packLine = packName ? `\nPathway: ${packName}` : '';

  const reasonsBlock =
    notYetReasons.length > 0
      ? notYetReasons.map((r) => `  • ${r}`).join('\n')
      : '  • Referral did not meet current criteria for this specialty';

  const missingSection =
    missingItemTypeLabels.length > 0
      ? [
          `The following information may change this determination if provided:`,
          ``,
          missingItemTypeLabels.map((t) => `  • ${t}`).join('\n'),
          ``,
        ].join('\n')
      : '';

  return [
    `NOT YET — REFERRAL CRITERIA NOT MET`,
    `Reference: ${ref}`,
    ``,
    `Specialty: ${specialtyName}${packLine}`,
    `Assessed: ${dateStr}`,
    ``,
    `This referral does not currently meet the criteria for a`,
    `${specialtyName} appointment.`,
    ``,
    `REASON(S):`,
    ``,
    reasonsBlock,
    ``,
    ...(missingSection ? [missingSection] : []),
    `OPTIONS FOR THE REFERRING PROVIDER:`,
    `  • Gather missing documentation and resubmit`,
    `  • Contact our intake team to discuss the case`,
    `  • Consider alternative care pathways`,
    ``,
    `Reference: ${ref}`,
    `Generated by ReferralGate (NO PHI).`,
  ].join('\n');
}
